const MAGIC_LINK_STORAGE_KEY = 'collegeryde_pending_magic_link_token';

const extractMagicLinkToken = (url: URL) => {
  const searchToken =
    url.searchParams.get('token') ||
    url.searchParams.get('access_token') ||
    url.searchParams.get('token_hash');

  if (searchToken) {
    return searchToken;
  }

  const hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
  if (!hash) {
    return null;
  }

  const hashParams = new URLSearchParams(hash);
  return (
    hashParams.get('token') ||
    hashParams.get('access_token') ||
    hashParams.get('token_hash')
  );
};

const persistMagicLinkToken = (token: string | null) => {
  if (!token) return;
  sessionStorage.setItem(MAGIC_LINK_STORAGE_KEY, token);
};

// Capture the token as early as possible in case a redirect or router update strips it.
if (typeof window !== 'undefined') {
  persistMagicLinkToken(extractMagicLinkToken(new URL(window.location.href)));
}

export const getMagicLinkTokenFromUrl = (url: URL = new URL(window.location.href)) => {
  const token = extractMagicLinkToken(url);
  if (token) {
    persistMagicLinkToken(token);
    return token;
  }

  return sessionStorage.getItem(MAGIC_LINK_STORAGE_KEY);
};

const inFlightMagicLinkRequests = new Map<string, Promise<unknown>>();

export const dedupeMagicLinkRequest = <T>(key: string, factory: () => Promise<T>): Promise<T> => {
  const existingRequest = inFlightMagicLinkRequests.get(key);
  if (existingRequest) {
    return existingRequest as Promise<T>;
  }

  const request = factory().catch((error) => {
    inFlightMagicLinkRequests.delete(key);
    throw error;
  });

  inFlightMagicLinkRequests.set(key, request);
  return request;
};

export const clearMagicLinkParamsFromUrl = () => {
  const url = new URL(window.location.href);
  const keys = ['token', 'access_token', 'token_hash', 'type', 'expires_at', 'expires_in', 'refresh_token'];

  sessionStorage.removeItem(MAGIC_LINK_STORAGE_KEY);

  keys.forEach((key) => {
    url.searchParams.delete(key);
  });

  if (url.hash) {
    const hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
    const hashParams = new URLSearchParams(hash);
    let changed = false;

    keys.forEach((key) => {
      if (hashParams.has(key)) {
        hashParams.delete(key);
        changed = true;
      }
    });

    url.hash = changed ? hashParams.toString() : '';
  }

  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, document.title, nextUrl || '/');
};
